import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import React, { Suspense } from "react";
import { Text } from "src/components/base/text";
import { Subheading } from "src/components/base/heading";
import { Button } from "src/components/base/button";
import { EllipsisVerticalIcon, NoSymbolIcon, PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { ApiInternal } from "src/api/api_internal";
import { FullSplitDnsDomain, FullSplitDnsResolver } from "src/api/generated/frontend_internal_v1";
import AddDomainDialog from "src/components/dialogs/add-domain";
import UpdateDomainDialog from "src/components/dialogs/update-domain";
import AddResolverDialog from "src/components/dialogs/add-resolver";
import UpdateResolverDialog from "src/components/dialogs/update-resolver";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { Badge } from "src/components/base/badges/base";
import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import DeleteResolverDialog from "src/components/dialogs/delete-resolver";
import DeleteDomainDialog from "src/components/dialogs/delete-domain";

/**
 * The properties for {@link SplitDnsOverview}
 */
export type SplitDnsOverviewProps = {};

/**
 * Overview for split Dns
 */
export default function SplitDnsOverview(props: SplitDnsOverviewProps) {
    const [t] = useTranslation("admin-dns");

    const [openAddDomain, setOpenAddDomain] = React.useState(false);
    const [openUpdateDomain, setOpenUpdateDomain] = React.useState<FullSplitDnsDomain | undefined>(undefined);
    const [openAddResolver, setOpenAddResolver] = React.useState<FullSplitDnsDomain | undefined>(undefined);
    const [openUpdateResolver, setOpenUpdateResolver] = React.useState<FullSplitDnsResolver | undefined>(undefined);
    const [openDeleteDomain, setOpenDeleteDomain] = React.useState<FullSplitDnsDomain | undefined>(undefined);
    const [openDeleteResolver, setOpenDeleteResolver] = React.useState<FullSplitDnsResolver | undefined>(undefined);

    const router = useRouter();
    const data = Route.useLoaderData();
    if (!data) {
        return;
    }

    return (
        <div className={"flex flex-col gap-8"}>
            <div className={"flex justify-between"}>
                <div className={"flex flex-col gap-1"}>
                    <Subheading>{t("heading.split-dns")}</Subheading>
                    <Text>{t("description.split-dns")}</Text>
                </div>
                <Button onClick={() => setOpenAddDomain(true)} className={"self-end"}>
                    <PlusIcon />
                    {t("button.add-domain")}
                </Button>
            </div>
            {data.list.map((domain) => (
                <div className={"flex flex-col gap-2"} key={domain.uuid}>
                    <div className={"flex justify-between"}>
                        <Subheading className={"flex items-center gap-2"}>
                            <span>{domain.domain}</span>
                            {domain.disabled && (
                                <Badge color={"zinc"}>
                                    <NoSymbolIcon className={"size-4"} />
                                    <span>{t("label.disabled")}</span>
                                </Badge>
                            )}
                        </Subheading>
                        <Dropdown>
                            <DropdownButton plain={true}>
                                <EllipsisVerticalIcon />
                            </DropdownButton>
                            <DropdownMenu anchor={"bottom end"}>
                                <DropdownSection>
                                    <DropdownItem onClick={() => setOpenAddResolver(domain)}>
                                        <PlusIcon />
                                        {t("button.add-resolver")}
                                    </DropdownItem>
                                    <DropdownItem onClick={() => setOpenUpdateDomain(domain)}>
                                        <PencilSquareIcon />
                                        {t("button.edit-domain")}
                                    </DropdownItem>
                                </DropdownSection>
                                <DropdownSection>
                                    <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>
                                    <DropdownItem onClick={() => setOpenDeleteDomain(domain)}>
                                        <TrashIcon />
                                        {t("button.delete-domain")}
                                    </DropdownItem>
                                </DropdownSection>
                            </DropdownMenu>
                        </Dropdown>
                    </div>
                    {domain.resolver.length > 0 && (
                        <Table className={"mb-4"}>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t("label.address")}</TableHeader>
                                    <TableHeader>{t("label.description")}</TableHeader>
                                    <TableHeader className={"w-0"}></TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {domain.resolver.map((resolver, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>
                                            <div className={"flex items-center gap-2"}>
                                                <span>{resolver.ip}</span>
                                                {resolver.disabled && (
                                                    <Badge color={"zinc"}>
                                                        <NoSymbolIcon className={"size-4"} />
                                                        <span>{t("label.disabled")}</span>
                                                    </Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>{resolver.description}</TableCell>
                                        <TableCell>
                                            <Dropdown>
                                                <DropdownButton plain={true}>
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor={"bottom end"}>
                                                    <DropdownSection>
                                                        <DropdownItem
                                                            onClick={() => {
                                                                setOpenUpdateResolver(resolver);
                                                            }}
                                                        >
                                                            <PencilSquareIcon />

                                                            {t("button.edit-resolver")}
                                                        </DropdownItem>
                                                    </DropdownSection>
                                                    <DropdownSection>
                                                        <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>
                                                        <DropdownItem onClick={() => setOpenDeleteResolver(resolver)}>
                                                            <TrashIcon />

                                                            {t("button.delete-resolver")}
                                                        </DropdownItem>
                                                    </DropdownSection>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            ))}
            {openAddDomain && (
                <Suspense>
                    <AddDomainDialog
                        onClose={() => setOpenAddDomain(false)}
                        onSubmit={async () => {
                            setOpenAddDomain(false);
                            await router.invalidate({ sync: true });
                        }}
                    />
                </Suspense>
            )}
            {openAddResolver && (
                <Suspense>
                    <AddResolverDialog
                        domainUuid={openAddResolver.uuid}
                        onClose={() => setOpenAddResolver(undefined)}
                        onSubmit={async () => {
                            setOpenAddResolver(undefined);
                            await router.invalidate({ sync: true });
                        }}
                    />
                </Suspense>
            )}

            {openUpdateDomain && (
                <Suspense>
                    <UpdateDomainDialog
                        domain={openUpdateDomain}
                        onClose={() => setOpenUpdateDomain(undefined)}
                        onSubmit={async () => {
                            setOpenUpdateDomain(undefined);
                            await router.invalidate({ sync: true });
                        }}
                    />
                </Suspense>
            )}
            {openUpdateResolver && (
                <Suspense>
                    <UpdateResolverDialog
                        resolver={openUpdateResolver}
                        onClose={() => setOpenUpdateResolver(undefined)}
                        onSubmit={async () => {
                            setOpenUpdateResolver(undefined);
                            await router.invalidate({ sync: true });
                        }}
                    />
                </Suspense>
            )}
            {openDeleteResolver && (
                <Suspense>
                    <DeleteResolverDialog
                        resolver={openDeleteResolver}
                        onClose={() => setOpenDeleteResolver(undefined)}
                        onDeletion={async () => {
                            setOpenDeleteResolver(undefined);
                            await router.invalidate({ sync: true });
                        }}
                    />
                </Suspense>
            )}
            {openDeleteDomain && (
                <Suspense>
                    <DeleteDomainDialog
                        domain={openDeleteDomain}
                        onClose={() => setOpenDeleteDomain(undefined)}
                        onDeletion={async () => {
                            setOpenDeleteDomain(undefined);
                            await router.invalidate({ sync: true });
                        }}
                    />
                </Suspense>
            )}
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/dns/_dns/split")({
    component: SplitDnsOverview,
    loader: async () => await ApiInternal.dns.getAllSplitDns(),
});
